<script lang="ts">
  import { onMount } from 'svelte';
  import AnimatedBackground from './lib/AnimatedBackground.svelte';
  import Navigation from './lib/Navigation.svelte';
  import ProjectCard from './lib/ProjectCard.svelte';
  import BlogCard from './lib/BlogCard.svelte';
  import { Github, ArrowRight, Code, Palette, Users, Youtube, MessageCircle } from 'lucide-svelte';
  import profileImage from './assets/enkei64.png';
  import caplaygroundImage from './assets/CAPlayground.png';
  import openPosterImage from './assets/OpenPoster.png';
  import Footer from './lib/Footer.svelte';
  
  const projects = [
    {
      title: "CAPlayground",
      description: "A collection of scripts to edit .ca files, all connected through a GUI powered by hope, dreams, and a bit of code.",
      technologies: ["Next.js", "TypeScript"],
      url: "https://caplayground.pages.dev",
      image: caplaygroundImage
    },
    {
      title: "OpenPoster",
      description: "A collection of scripts to edit .ca files, all connected through a GUI powered by hope, dreams, and a bit of code.",
      technologies: ["Python"],
      url: "https://github.com/openposter",
      image: openPosterImage
    },
  ];
  
  const blogPosts = [
    {
      title: "Using Nugget MobileGestalt tweaks",
      excerpt: "Learn how to use MobileGestalt tweaks to enable restricted iOS features on unsupported devices.",
      date: "June 8, 2025",
      readTime: "3 min read",
      tags: ["iOS", "Tweaks", "MobileGestalt"],
      url: "/#/blog/using-nugget-mobilegestalt-tweaks"
    },
    {
      title: "Welcome to enkei64's portfolio!",
      excerpt: "An introduction to my portfolio on my website and how it was made.",
      date: "June 8, 2025",
      readTime: "2 min read",
      tags: ["Portfolio", "Development", "Introduction"],
      url: "/#/blog/welcome-to-enkei64-portfolio"
    }
  ];
  
  let mounted = false;
  
  onMount(() => {
    mounted = true;

    if (!('scrollBehavior' in document.documentElement.style)) {
      import('smoothscroll-polyfill').then(smoothscroll => {
        smoothscroll.polyfill();
      });
    }
  });
</script>

<svelte:head>
  <title>enkei64 - Portfolio</title>
  <meta name="description" content="Developer passionate in crafting fast, reliable and modern websites and apps." />
  
  <!-- Apple Web App Meta Tags -->
  <meta name="apple-mobile-web-app-capable" content="yes" />
  <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
  <meta name="apple-mobile-web-app-title" content="enkei64" />
  <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
  
  <!-- Open Graph / Social Media Meta Tags -->
  <meta property="og:title" content="enkei64 - Web & App Developer" />
  <meta property="og:description" content="Developer passionate in crafting fast, reliable and modern websites and apps." />
  <meta property="og:image" content="/og-image.png" />
  <meta property="og:type" content="website" />
  
  <!-- Twitter Card -->
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="enkei64 - Web & App Developer" />
  <meta name="twitter:description" content="Developer passionate in crafting fast, reliable and modern websites and apps." />
  <meta name="twitter:image" content="/og-image.png" />
</svelte:head>

<AnimatedBackground />

<div class="app-layout">
  <Navigation />
  
  <main class="main-content">
    <!-- Introduction Section -->
    <section id="introduction" class="section">
      <div class="container">
        <div class="intro-content">
          <div class="intro-text" class:animate={mounted}>
            <h1>
              <span>Hi, I'm<br />
              <span class="text-gradient">enkei64.</span></span>
              <div class="mobile-profile-image">
                <img src={profileImage} alt="enkei64" />
              </div>
            </h1>
            <p class="intro-description">
              I'm a Web & App Developer passionate about creating beautiful, functional websites and apps.
              Currently putting most of my developing time into CAPlayground.
            </p>
            <div class="intro-stats">
              <div class="stat-item glass">
                <Code size={24} />
                <div>
                  <h3>14</h3>
                  <p>Projects Completed</p>
                </div>
              </div>
              <div class="stat-item glass">
                <Palette size={24} />
                <div>
                  <h3>2+</h3>
                  <p>Years Experience</p>
                </div>
              </div>
              <div class="stat-item glass">
                <Users size={24} />
                <div>
                  <h3>2</h3>
                  <p>Happy Collaborators</p>
                </div>
              </div>
            </div>
            <div class="intro-actions">
              <a href="#projects" class="btn">
                <Github size={16} />
                View My Work
              </a>
            </div>
            <div class="social-links">
              <a href="https://github.com/enkei64" target="_blank" rel="noopener noreferrer" class="social-link">
                <Github size={20} />
              </a>
              <a href="https://discordapp.com/users/1262238431858262057" target="_blank" rel="noopener noreferrer" class="social-link">
                <MessageCircle size={20} />
              </a>
              <a href="https://youtube.com/@enkei64" target="_blank" rel="noopener noreferrer" class="social-link">
                <Youtube size={20} />
              </a>
            </div>
          </div>
          <div class="intro-visual" class:animate={mounted}>
            <div class="profile-card glass-card">
              <div class="profile-image">
                <img src={profileImage} alt="enkei64" />
              </div>
              <div class="profile-info">
                <h3>enkei64</h3>
                <p>Web & App Developer</p>
                <div class="skills">
                  <span>TypeScript</span>
                  <span>Swift</span>
                  <span>HTML</span>
                  <span>Tailwind CSS</span>
                  <span>JavaScript</span>
                  <span>Python</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Projects Section -->
    <section id="projects" class="section">
      <div class="container">
        <div class="section-header" class:animate={mounted}>
          <h2>Public Projects</h2>
          <p>A selection of my recent work and open-source contributions</p>
        </div>
        <div class="grid grid-2 projects-grid">
          {#each projects as project, index}
            <div class="project-item" class:animate={mounted} style="animation-delay: {index * 0.1}s">
              <ProjectCard {...project} />
            </div>
          {/each}
        </div>
      </div>
    </section>
    
    <!-- Blog Section -->
    <section id="blog" class="section">
      <div class="container">
        <div class="section-header" class:animate={mounted}>
          <h2>Latest Articles</h2>
          <p>Thoughts on development, iOS tweaks, and technology</p>
        </div>
        <div class="grid grid-3 blog-grid">
          {#each blogPosts as post, index}
            <div class="blog-item" class:animate={mounted} style="animation-delay: {index * 0.1}s">
              <BlogCard {...post} />
            </div>
          {/each}
        </div>
        <div class="blog-cta" class:animate={mounted}>
          <a href="/#/blog" class="btn">
            View All Articles
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
    <Footer />
  </main>
</div>

<style>
  .app-layout {
    display: flex;
  }
  
  .main-content {
    margin-left: 0;
    padding-left: calc(var(--nav-width) + var(--spacing-2xl));
    position: relative;
    z-index: 1;
    width: 100%;
  }
  
  .container {
    max-width: 1200px;
    width: 100%;
    margin: 0 auto;
    padding: 0 var(--spacing-lg);
  }
  
  .intro-content {
    display: grid;
    grid-template-columns: 1fr 400px;
    gap: var(--spacing-2xl);
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    max-width: 1000px;
    margin: 0 auto;
  }
  
  .intro-text h1 {
    margin-bottom: var(--spacing-lg);
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
  }
  
  .mobile-profile-image {
    display: none;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid var(--accent-orange);
    margin-left: auto;
  }
  
  .mobile-profile-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .intro-description {
    font-size: clamp(1.125rem, 2vw, 1.25rem);
    margin-bottom: var(--spacing-xl);
    max-width: 600px;
  }
  
  .intro-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--spacing-md);
    margin-bottom: var(--spacing-xl);
  }
  
  .stat-item {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    padding: var(--spacing-md);
    border-radius: var(--border-radius-small);
  }
  
  .stat-item h3 {
    font-size: 1.5rem;
    margin: 0;
    color: var(--accent-orange);
  }
  
  .stat-item p {
    font-size: 0.875rem;
    margin: 0;
    color: var(--text-secondary);
  }
  
  .intro-actions {
    display: flex;
    gap: var(--spacing-md);
    margin-bottom: var(--spacing-xl);
  }
  
  .social-links {
    display: flex;
    gap: var(--spacing-md);
  }
  
  .social-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    background: var(--glass-bg);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid var(--glass-border);
    border-radius: 50%;
    color: var(--text-secondary);
    text-decoration: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .social-link:hover {
    color: var(--accent-orange);
    border-color: var(--accent-orange);
    transform: translateY(-2px);
    box-shadow: 0 10px 20px var(--accent-orange-glow);
  }
  
  .profile-card {
    padding: var(--spacing-lg);
    text-align: center;
    max-width: 300px;
    margin: 0 auto;
  }
  
  .profile-image {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    overflow: hidden;
    margin: 0 auto var(--spacing-md);
    border: 3px solid var(--accent-orange);
  }
  
  .profile-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .profile-info h3 {
    margin-bottom: var(--spacing-xs);
  }
  
  .profile-info p {
    color: var(--text-secondary);
    margin-bottom: var(--spacing-md);
  }
  
  .skills {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-xs);
    justify-content: center;
  }
  
  .skills span {
    padding: 0.25rem 0.75rem;
    background: rgba(90, 209, 151, 0.1);
    border: 1px solid rgba(90, 209, 151, 0.25);
    border-radius: 20px;
    font-size: 0.875rem;
    color: var(--accent-orange);
  }
  
  .section-header {
    text-align: center;
    margin-bottom: var(--spacing-2xl);
  }
  
  .section-header p {
    font-size: 1.125rem;
    max-width: 600px;
    margin: 0 auto;
  }
  
  .blog-cta {
    text-align: center;
    margin-top: var(--spacing-2xl);
  }
  
  .animate {
    animation: fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    opacity: 0;
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: 1086px) {
    .intro-visual {
      display: none;
    }
    
    .intro-content {
      grid-template-columns: 1fr;
      max-width: 800px;
    }
    
    .mobile-profile-image {
      display: block;
    }
  }
  
  @media (max-width: 596px) {
    .intro-stats {
      grid-template-columns: 1fr;
      gap: var(--spacing-sm);
    }
    
    .stat-item {
      width: 100%;
      padding: var(--spacing-sm);
    }
    
    .intro-actions {
      flex-direction: column;
      width: 100%;
      align-items: stretch;
    }
    
    .intro-actions .btn {
      width: 100%;
      justify-content: center;
    }
  }
  
  @media (max-width: 768px) {
    .main-content {
      margin-left: 0;
      padding-left: 0;
      padding-bottom: 80px;
    }
    
    .container {
      padding: 0 var(--spacing-lg);
    }
  }
</style>
